import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Check, Loader2 } from "lucide-react";
import { getStoreTabDays, updateStoreTabDays } from "../../services/AppConfigService";

const RETAILER_NAMES: Record<string, string> = {
  walmart: "Walmart",
  home_depot: "Home Depot",
  target: "Target",
  dollar_general: "Dollar General",
  bestbuy: "Best Buy",
  costco: "Costco",
  lowes: "Lowe's",
  cvs: "CVS",
  tractor_supply: "Tractor Supply",
  northern_tool: "Northern Tool",
  samsclub: "Sam's Club",
};

const windowLabel = (days: number) => (days === 1 ? "last 24 hours" : `last ${days} days`);

export default function StoreTabDaysSettings() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, number> | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["store-tab-days"],
    queryFn: getStoreTabDays,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data && draft === null) setDraft(data.days);
  }, [data, draft]);

  const mutation = useMutation({
    mutationFn: updateStoreTabDays,
    onSuccess: (next) => {
      queryClient.setQueryData(["store-tab-days"], next);
      setDraft(next.days);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (isLoading || !data || !draft) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const inRange = (days: number) => Number.isInteger(days) && days >= data.minDays && days <= data.maxDays;
  const dirty = data.retailers.some(({ retailer }) => draft[retailer] !== data.days[retailer]);
  const valid = data.retailers.every(({ retailer }) => inRange(draft[retailer]));

  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="leading-tight">
          <div className="flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5 text-gray-500 dark:text-dark-400" />
            <h2 className="text-[13px] font-bold text-gray-900 dark:text-white">Store tab window</h2>
          </div>
          <p className="mt-0.5 text-[11px] text-gray-400 dark:text-dark-400">
            How many days of crawled deals every user sees on the store tab. Applies to all users within about a minute. Penny mode always shows 45 days.
          </p>
        </div>
        <button
          type="button"
          onClick={() => mutation.mutate(draft)}
          disabled={!dirty || !valid || mutation.isPending}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          {mutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3" /> : null}
          {saved ? "Saved" : dirty ? "Save window" : "Saved"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {data.retailers.map(({ retailer, defaultDays }) => {
          const value = draft[retailer];
          const changed = value !== data.days[retailer];
          return (
            <label
              key={retailer}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-dark-700 dark:bg-dark-800"
            >
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
                  {RETAILER_NAMES[retailer] ?? retailer}
                  {changed && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-dark-400">
                  {inRange(value) ? windowLabel(value) : "—"}
                  {value !== defaultDays && ` · default ${defaultDays}`}
                </span>
              </span>
              <span className="flex items-center gap-1 text-xs font-semibold text-gray-600 dark:text-dark-300">
                <input
                  type="number"
                  min={data.minDays}
                  max={data.maxDays}
                  step="1"
                  aria-label={`${RETAILER_NAMES[retailer] ?? retailer} store tab days`}
                  value={Number.isNaN(value) ? "" : value}
                  onChange={(e) => setDraft({ ...draft, [retailer]: Math.round(Number(e.target.value)) })}
                  className={`w-16 rounded-md border bg-white px-2 py-1 text-right font-mono text-xs text-gray-900 dark:bg-dark-800 dark:text-white ${
                    inRange(value) ? "border-gray-200 dark:border-dark-600" : "border-amber-500"
                  }`}
                />
                <span>{value === 1 ? "day" : "days"}</span>
              </span>
            </label>
          );
        })}
      </div>

      {!valid && (
        <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
          Each window must be a whole number between {data.minDays} and {data.maxDays} days.
        </p>
      )}
      {mutation.isError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">Failed to save</p>}
      <p className="mt-3 text-[11px] text-gray-400 dark:text-dark-400">
        Walmart and Home Depot stores with fewer than 25 deals in a window shorter than 3 days still fall back to their last 3 days.
      </p>
    </div>
  );
}
